/// <reference path="../../common/common.module.ts" />

/**
 * Web Component Registry Service
 *
 * Loads enrolled web component definitions from a JSON manifest and provides
 * lookup, feature gate evaluation, and gate source extensibility.
 *
 * Registered on `meevo.components.common` so it is available to all plugins.
 *
 * --- Adding a new web component ---
 * 1. Add an enrollment entry to config/webComponentRegistry.json
 * 2. Create a bundle loader service (see appointmentBookWcLoader for reference)
 * 3. If your feature gate uses a new source, register it at bootstrap:
 *      webComponentRegistry.registerGateSource('mySource', function() {
 *          return myService.getMyData();
 *      });
 * 4. Use <web-component-mount component-id="your-id"> in your template
 */

type FeatureGateType = 'boolean-path';

interface WebComponentFeatureGate {
    type: FeatureGateType;
    source: string;
    path: string;
    defaultValue?: boolean;
}

interface WebComponentEnrollment {
    id: string;
    displayName?: string;
    customElementTag: string;
    bundleLoaderService?: string;
    mountSelector?: string;
    hostBridgeInput?: string;
    apiInput?: string;
    apiContextPath?: string;
    featureGate?: WebComponentFeatureGate;
    legacyTemplateUrl?: string;
    legacyController?: string;
    eventHandlers?: { [eventName: string]: any };
}

interface WebComponentRegistryDocument {
    version: string;
    components: WebComponentEnrollment[];
}

const WC_REGISTRY_URL = 'app/components/webComponents/config/webComponentRegistry.json';

const WC_EMPTY_REGISTRY: WebComponentRegistryDocument = {
    version: '2.0.0',
    components: [],
};

angular.module('meevo.components.common').factory('webComponentRegistry', [
    '$http',
    '$q',
    function ($http: ng.IHttpService, $q: ng.IQService) {
        var registryPromise: ng.IPromise<WebComponentRegistryDocument> | null = null;
        var gateSources: { [sourceName: string]: () => ng.IPromise<any> | any } = {};

        function getPathValue(source: any, path: string | undefined): any {
            if (!source || !path) {
                return undefined;
            }

            var segments = path.split('.').filter(Boolean);
            var current: any = source;
            for (var i = 0; i < segments.length; i++) {
                if (current == null || typeof current !== 'object') {
                    return undefined;
                }
                current = current[segments[i]];
            }
            return current;
        }

        function normalizeRegistry(candidate: Partial<WebComponentRegistryDocument> | null | undefined): WebComponentRegistryDocument {
            if (!candidate || !Array.isArray(candidate.components)) {
                console.warn('[webComponentRegistry] Invalid registry document. Returning empty registry.');
                return WC_EMPTY_REGISTRY;
            }

            return {
                version: String(candidate.version || '2.0.0'),
                components: candidate.components
                    .filter(function (component): component is WebComponentEnrollment {
                        return !!component &&
                            typeof component.id === 'string' &&
                            typeof component.customElementTag === 'string';
                    })
                    .map(function (component) {
                        return {
                            ...component,
                            mountSelector: component.mountSelector || '[data-web-component-host]',
                            hostBridgeInput: component.hostBridgeInput || 'hostBridge',
                            apiInput: component.apiInput || 'api',
                            apiContextPath: component.apiContextPath || 'apiBaseUrl',
                        };
                    }),
            };
        }

        function loadRegistry(): ng.IPromise<WebComponentRegistryDocument> {
            if (registryPromise) {
                return registryPromise;
            }

            registryPromise = $http
                .get<WebComponentRegistryDocument>(WC_REGISTRY_URL, { cache: true })
                .then(function (response) {
                    var registry = normalizeRegistry(response.data);
                    console.log('[webComponentRegistry] Loaded ' + registry.components.length + ' enrollment(s), version ' + registry.version);
                    return registry;
                })
                .catch(function (error) {
                    console.warn('[webComponentRegistry] Failed to load registry JSON. No web components will be available.', error);
                    return WC_EMPTY_REGISTRY;
                });

            return registryPromise;
        }

        function getEnrollment(componentId: string): ng.IPromise<WebComponentEnrollment> {
            return loadRegistry().then(function (registry) {
                var enrollment = registry.components.find(function (entry) { return entry.id === componentId; });

                if (!enrollment) {
                    return $q.reject('[webComponentRegistry] Enrollment not found: ' + componentId);
                }

                return enrollment;
            });
        }

        /**
         * Register a gate source resolver. Call this at plugin bootstrap time.
         *
         * Example:
         *   webComponentRegistry.registerGateSource('businessInfoPreferences', function() {
         *       return businessInfoDataService.getBusinessInfoPreferences();
         *   });
         */
        function registerGateSource(sourceName: string, resolver: () => ng.IPromise<any> | any): void {
            if (gateSources[sourceName]) {
                console.warn('[webComponentRegistry] Overwriting gate source: ' + sourceName);
            }
            gateSources[sourceName] = resolver;
        }

        function isEnabled(componentId: string): ng.IPromise<boolean> {
            return getEnrollment(componentId).then(function (enrollment) {
                var gate = enrollment.featureGate;
                if (!gate) {
                    return true;
                }

                if (gate.type !== 'boolean-path') {
                    console.warn('[webComponentRegistry] Unknown gate type: ' + gate.type + '. Using defaultValue.');
                    return gate.defaultValue !== false;
                }

                var sourceResolver = gateSources[gate.source];
                if (!sourceResolver) {
                    console.warn('[webComponentRegistry] No gate source registered for: ' + gate.source +
                        '. Register it via webComponentRegistry.registerGateSource(). Using defaultValue.');
                    return gate.defaultValue === true;
                }

                return $q.resolve(sourceResolver()).then(function (sourceValue: any) {
                    var resolved = getPathValue(sourceValue, gate.path);
                    if (typeof resolved === 'boolean') {
                        return resolved;
                    }
                    return gate.defaultValue === true;
                });
            });
        }

        return {
            load: loadRegistry,
            getEnrollment: getEnrollment,
            isEnabled: isEnabled,
            registerGateSource: registerGateSource,
        };
    }
]);
